function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const { differenceInSeconds } = require("date-fns");
const { getAsync, setAsync, delAsync } = require("../elasticache-service/elasticache-service");
const { initLogger } = require("../logger-service/logger-service");
const Sentry = require("@sentry/nextjs");
// Per-pod registry of in-flight upstream fetches keyed by cache key.
// Coalesces concurrent stale reads so only one upstream fetch fires per key,
// avoiding cache-stampede memory/latency spikes when many requests hit a stale entry simultaneously.
const inflightFetches = new Map();
// Bounds how long a single upstream fetch can hold the in-flight slot.
// Without this, a hung upstream call would wedge every coalesced caller indefinitely
// and block all future stale reads until the pod restarts.
const FETCH_TIMEOUT_MS = 5000;
// ioredis queues commands while a node is unreachable and never rejects them, so bound every cache call.
const CACHE_OP_TIMEOUT_MS = 1000;
const withTimeout = (promise, ms, message)=>Promise.race([
        promise,
        new Promise((_, reject)=>setTimeout(()=>reject(new Error(message)), ms).unref())
    ]);
const toCacheable = (provider)=>{
    // Provider and cache properties
    const key = provider.key();
    const params = provider.params();
    const refreshInterval = provider.refreshInterval();
    const cacheExpirationTime = provider.cacheExpirationTime();
    // Cache keys
    const CACHE_PREFIX = "homepage::cache_service";
    const cacheKey = `${CACHE_PREFIX}::${key}`;
    // Logger init
    const logger = initLogger();
    const metadata = {
        provider: key,
        params,
        cacheKey
    };
    const shouldFetch = (value)=>{
        const lastUpdatedAt = value && value.lastUpdatedAt;
        return differenceInSeconds(new Date(), lastUpdatedAt) > refreshInterval;
    };
    const withCacheTimeout = (promise, op)=>withTimeout(promise, CACHE_OP_TIMEOUT_MS, `cacheService:::${op} timed out after ${CACHE_OP_TIMEOUT_MS}ms for ${key}`);
    // The cache is disposable, so an unreachable one should cost a refetch, not the request.
    const readCache = function() {
        var _ref = _async_to_generator(function*() {
            try {
                return yield withCacheTimeout(getAsync(cacheKey), "read");
            } catch (error) {
                logger.error(_object_spread_props(_object_spread({}, metadata), {
                    error
                }), `cacheService:::read failed, treating as a cache miss`);
                return null;
            }
        });
        return function readCache() {
            return _ref.apply(this, arguments);
        };
    }();
    const writeCache = function() {
        var _ref = _async_to_generator(function*(cacheData) {
            try {
                yield withCacheTimeout(setAsync(cacheKey, cacheData, "EX", cacheExpirationTime), "write");
            } catch (error) {
                logger.error(_object_spread_props(_object_spread({}, metadata), {
                    error
                }), `cacheService:::write failed, serving uncached data`);
            }
        });
        return function writeCache(cacheData) {
            return _ref.apply(this, arguments);
        };
    }();
    const invalidate = function() {
        var _ref = _async_to_generator(function*() {
            try {
                yield withCacheTimeout(delAsync(cacheKey), "invalidate");
            } catch (error) {
                logger.error(_object_spread_props(_object_spread({}, metadata), {
                    error
                }), `cacheService:::invalidate failed`);
                throw error;
            }
        });
        return function invalidate() {
            return _ref.apply(this, arguments);
        };
    }();
    const fetch = function() {
        var _ref = _async_to_generator(function*() {
            const data = yield withTimeout(provider.getData(), FETCH_TIMEOUT_MS, `cacheService:::fetch timed out after ${FETCH_TIMEOUT_MS}ms for ${key}`);
            if (!provider.validate(data)) {
                Sentry.captureMessage(`Failed to validate new data in cache-service for ${key}}`);
                throw new Error("Validation failed for new data");
            }
            const cacheData = JSON.stringify({
                data,
                lastUpdatedAt: new Date()
            });
            yield writeCache(cacheData);
            return data;
        });
        return function fetch() {
            return _ref.apply(this, arguments);
        };
    }();
    const fetchOnce = ()=>{
        const existing = inflightFetches.get(cacheKey);
        if (existing) {
            logger.info(metadata, "cacheService:::fetch coalesced with in-flight fetch");
            return existing;
        }
        const promise = fetch().finally(()=>inflightFetches.delete(cacheKey));
        inflightFetches.set(cacheKey, promise);
        return promise;
    };
    const getData = function() {
        var _ref = _async_to_generator(function*() {
            try {
                const cacheValue = yield readCache();
                let response = null;
                // Cache hit
                if (cacheValue) {
                    logger.info(metadata, "cacheService:::fetch cache hit");
                    let value;
                    let forceFetch = false;
                    // Try to parse the value in cache, if it fails, try to regenerate in order to get a new copy
                    // Might happen with corrupted json received from getData
                    try {
                        value = JSON.parse(cacheValue);
                        response = value.data;
                    } catch (error) {
                        logger.error(_object_spread_props(_object_spread({}, metadata), {
                            error
                        }), `cacheService:::fetch forced regenerating because of invalid json`);
                        forceFetch = true;
                    }
                    // Check if should be refreshed, even if value exists in cache
                    if (forceFetch || shouldFetch(value)) {
                        if (forceFetch) {
                            // Corrupted JSON — response is null, must block to get usable data
                            logger.info(_object_spread_props(_object_spread({}, metadata), {
                                forceFetch
                            }), "cacheService:::fetch re-fetching (force)");
                            try {
                                response = yield fetchOnce();
                            } catch (error) {
                                logger.error(_object_spread_props(_object_spread({}, metadata), {
                                    forceFetch,
                                    error
                                }), `cacheService:::fetch failed while re-fetching`);
                            }
                        } else {
                            // Stale-while-revalidate: return stale data immediately, refresh in background
                            logger.info(metadata, "cacheService:::fetch triggering background refresh (stale-while-revalidate)");
                            fetchOnce().catch((error)=>{
                                logger.error(_object_spread_props(_object_spread({}, metadata), {
                                    error
                                }), `cacheService:::fetch failed during background re-fetch`);
                            });
                        }
                    }
                } else {
                    logger.info(metadata, `cacheService:::fetch cache miss`);
                    response = yield fetchOnce();
                }
                return response;
            } catch (error) {
                logger.error(error, `cacheService:::getData failed`);
                throw error;
            }
        });
        return function getData() {
            return _ref.apply(this, arguments);
        };
    }();
    return {
        getData,
        invalidate
    };
};
module.exports = {
    toCacheable
};
